import { useState, useEffect } from 'react';
import { apiService } from '../../lib/api';

export default function PhiDetectionSettings() {
  const [enabled, setEnabled] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('gpt-4o');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settings = await apiService.request<Record<string, string>>('/api/v1/admin/phi-settings');
      setEnabled(settings.phi_detection_enabled !== 'false');
      setPrompt(settings.phi_detection_prompt || '');
      setModel(settings.phi_detection_model || 'gpt-4o');
    } catch (error) {
      console.error('Failed to load PHI settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      await apiService.request('/api/v1/admin/phi-settings', {
        method: 'PUT',
        body: JSON.stringify({
          phi_detection_enabled: enabled,
          phi_detection_prompt: prompt,
          phi_detection_model: model,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setMessage('Settings saved successfully');
    } catch (error) {
      setMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4">Loading PHI settings...</div>;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow">
      <h3 className="text-lg font-semibold mb-4">PHI Detection Settings</h3>

      <div className="mb-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="rounded"
          />
          <span>Enable PHI detection on uploads</span>
        </label>
        <p className="text-sm text-gray-500 mt-1">
          When enabled, all uploaded images and videos are scanned for patient faces, names, and other identifying information before being stored.
        </p>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">AI Model</label>
        <input
          type="text"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="w-full border rounded p-2 dark:bg-gray-700 dark:border-gray-600"
          placeholder="gpt-4o"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Detection Prompt</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={12}
          className="w-full border rounded p-2 font-mono text-sm dark:bg-gray-700 dark:border-gray-600"
          placeholder="Enter the system prompt for PHI detection..."
        />
        <p className="text-sm text-gray-500 mt-1">
          This prompt is sent to the AI model with each uploaded image. Adjust to control what is flagged.
        </p>
      </div>

      <div className="flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {message && (
          <span className={message.includes('Failed') ? 'text-red-500' : 'text-green-500'}>
            {message}
          </span>
        )}
      </div>
    </div>
  );
}
